import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api, { fmtDate, getPosition } from "@/api";
import { useAuth } from "@/AuthContext";
import { toast } from "sonner";
import { MapPin, ChevronRight, Power, Camera } from "lucide-react";

function DutyCamera({ onCapture, testid, label }) {
  const ref = useRef();
  return (
    <>
      <input ref={ref} type="file" accept="image/*" capture="user" className="hidden" data-testid={`${testid}-file`}
        onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; const r = new FileReader(); r.onload = () => onCapture(r.result); r.readAsDataURL(f); }} />
      <button type="button" data-testid={testid} onClick={() => ref.current.click()}
        className="w-full py-3.5 rounded-xl border-2 border-dashed border-border text-muted-foreground flex items-center justify-center gap-2 text-sm font-medium active:bg-surface-elevated">
        <Camera size={18} /> {label}
      </button>
    </>
  );
}

const J_STYLE = {
  Assigned: "border-primary/40 text-primary",
  "In Progress": "border-warning/40 text-warning",
  Completed: "border-success/40 text-success",
  Closed: "border-border text-muted-foreground",
};

export default function EmpHome() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [duty, setDuty] = useState({ on_duty: false, since: null });
  const [busy, setBusy] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [elapsed, setElapsed] = useState("");

  const load = useCallback(async () => {
    const [j, d] = await Promise.all([api.get("/jobs?mine=true"), api.get("/duty/status")]);
    setJobs(j.data);
    setDuty(d.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!duty.on_duty || !duty.since) { setElapsed(""); return; }
    const tick = () => {
      const ms = Date.now() - new Date(duty.since).getTime();
      setElapsed(`${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`);
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [duty]);

  const doDuty = async () => {
    setBusy(true);
    const pos = await getPosition();
    try {
      const r = await api.post(duty.on_duty ? "/duty/checkout" : "/duty/checkin", {
        type: duty.on_duty ? "end_duty" : "start_duty", lat: pos?.lat, lng: pos?.lng, photo_data: photo,
      });
      if (r.data.distance_m != null && !r.data.geofence_pass) {
        toast.warning(`Recorded, but you are ${Math.round(r.data.distance_m)}m from today's job site (flagged for review)`);
      } else {
        toast.success(duty.on_duty ? "Checked out. See you tomorrow!" : "Checked in. Have a great day!");
      }
      setPhoto(null); setCaptureOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const hr = new Date().getHours();
  const greeting = hr < 12 ? "Good Morning" : hr < 17 ? "Good Afternoon" : "Good Evening";
  const todayStr = new Date().toLocaleDateString("en-CA");
  const todays = jobs.filter((j) => j.scheduled_date === todayStr && ["Assigned", "In Progress"].includes(j.status));

  const active = jobs.filter((j) => ["Assigned", "In Progress"].includes(j.status));
  const past = jobs.filter((j) => ["Completed", "Closed"].includes(j.status));

  return (
    <div className="p-5 space-y-6" data-testid="emp-home">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</div>
          <h1 className="font-heading text-2xl font-bold" data-testid="greeting-text">{greeting}, {user.name.split(" ")[0]}</h1>
          <div className="text-xs mt-1 text-muted-foreground" data-testid="duty-status-line">
            {duty.on_duty
              ? <span className="text-success">Checked in since {new Date(duty.since).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}{elapsed && ` · ${elapsed}`}</span>
              : "You are currently Checked Out"}
          </div>
        </div>
        <div className="w-12 h-12 rounded-full bg-success/20 text-success flex items-center justify-center font-heading font-extrabold text-lg overflow-hidden shrink-0" data-testid="emp-avatar">
          {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : user.name?.[0]}
        </div>
      </div>

      {todays.length > 0 && (
        <div className="bg-surface border border-success/30 rounded-2xl p-4" data-testid="today-jobs-card">
          <div className="text-xs uppercase tracking-widest text-success mb-2">Today's Schedule</div>
          {todays.map((j) => (
            <Link key={j.id} to={`/app/job/${j.id}`} className="flex justify-between items-center text-sm py-1.5">
              <span className="truncate">{j.service} — {j.customer_name}</span>
              <span className="font-mono text-success shrink-0 ml-2">{j.scheduled_start_time || ""}</span>
            </Link>
          ))}
        </div>
      )}

      {!captureOpen ? (
        <button data-testid="duty-toggle-button" onClick={() => setCaptureOpen(true)}
          className={`w-full py-5 rounded-2xl font-heading font-extrabold text-lg flex items-center justify-center gap-3 transition-all active:scale-[0.98] shadow-lg ${duty.on_duty ? "bg-danger text-inverse shadow-danger/40" : "bg-primary text-primary-foreground shadow-primary/40"}`}>
          <Power size={22} /> {duty.on_duty ? "CHECK OUT" : "CHECK IN"}
        </button>
      ) : (
        <div className="bg-surface border border-border rounded-2xl p-4 space-y-3" data-testid="duty-capture-panel">
          <div className="font-heading font-bold">{duty.on_duty ? "Check Out" : "Check In"} — quick selfie + GPS</div>
          {photo && <img src={photo} alt="selfie" className="w-full h-36 object-cover rounded-xl border border-border" />}
          <DutyCamera testid="duty-selfie" label={photo ? "Retake Selfie" : "Take Selfie"} onCapture={setPhoto} />
          <div className="flex gap-2">
            <button data-testid="duty-cancel-button" onClick={() => { setCaptureOpen(false); setPhoto(null); }}
              className="flex-1 py-3.5 rounded-xl bg-surface-elevated border border-border font-bold">Cancel</button>
            <button data-testid="duty-confirm-button" onClick={doDuty} disabled={busy || !photo}
              className={`flex-1 py-3.5 rounded-xl font-extrabold disabled:opacity-40 ${duty.on_duty ? "bg-danger text-inverse" : "bg-primary text-primary-foreground"}`}>
              {busy ? "Locating…" : "CONFIRM"}
            </button>
          </div>
        </div>
      )}

      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">My Jobs ({active.length})</div>
        <div className="space-y-3" data-testid="emp-jobs-list">
          {active.map((j) => (
            <Link key={j.id} to={`/app/job/${j.id}`} data-testid={`emp-job-${j.id}`}
              className="block bg-surface border border-border rounded-2xl p-4 active:bg-surface-elevated transition-colors">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs text-muted-foreground">{j.job_no}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${J_STYLE[j.status]}`}>{j.status}</span>
              </div>
              <div className="font-heading font-bold mt-1.5">{j.service}</div>
              <div className="text-sm text-muted-foreground">{j.customer_name}</div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2">
                <MapPin size={12} /> {j.address || "Address on file"} · {fmtDate(j.scheduled_date)}
              </div>
              <div className="flex items-center justify-end text-primary text-xs font-semibold mt-2">Open <ChevronRight size={14} /></div>
            </Link>
          ))}
          {active.length === 0 && <div className="text-center text-muted-foreground text-sm py-10 bg-surface rounded-2xl border border-border">No active jobs assigned</div>}
        </div>
      </div>

      {past.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">History</div>
          <div className="space-y-2">
            {past.slice(0, 10).map((j) => (
              <Link key={j.id} to={`/app/job/${j.id}`} className="flex items-center justify-between bg-surface border border-border rounded-xl px-4 py-3 text-sm">
                <div><span className="font-mono text-xs text-muted-foreground">{j.job_no}</span><div className="text-muted-foreground">{j.service}</div></div>
                {j.rating ? <span className="text-warning-foreground text-xs font-bold">★ {j.rating}</span> : <span className="text-muted-foreground text-xs">{j.status}</span>}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
